import type { ColumnDef } from '@tanstack/react-table'
import { DataTableColumnHeader } from '@/shared/components/data-table'
import { cn } from '@/shared/lib/utils'
import { formatHumanDate } from '@/shared/utils/format-date'
import type { RoleAdminRow } from '../model/role-admin'

export const ROLES_ADMIN_COLUMN_NAMES: Record<string, string> = {
  name: 'Rol',
  permissions: 'Permisos',
  usersCount: 'Usuarios',
  active: 'Estado',
  updatedAt: 'Actualizado'
}

/**
 * Badge sobrio estilo Shopify: píldora neutra (gris) con un punto de color
 * discreto — el color vive en el punto, no grita en el fondo.
 */
export function RoleActiveBadge({ active }: { active: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground/80">
      <span
        aria-hidden="true"
        className={cn(
          'h-1.5 w-1.5 shrink-0 rounded-full',
          active ? 'bg-emerald-500' : 'bg-neutral-400'
        )}
      />
      {active ? 'Activo' : 'Inactivo'}
    </span>
  )
}

export const rolesAdminColumns: ColumnDef<RoleAdminRow>[] = [
  {
    accessorKey: 'name',
    header: ({ column }) => (
      <DataTableColumnHeader className="min-w-[220px]" column={column} name="Rol" />
    ),
    cell: ({ row }) => (
      <div className="flex min-w-0 flex-col">
        <span className="truncate font-medium" title={row.original.name}>
          {row.original.name}
        </span>
        {row.original.description && (
          <span className="truncate text-xs text-muted-foreground" title={row.original.description}>
            {row.original.description}
          </span>
        )}
      </div>
    )
  },
  {
    accessorKey: 'permissions',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} name="Permisos" sortable={false} />
    ),
    // Docs viejos pueden no traer el campo: siempre con fallback.
    cell: ({ row }) => (
      <span className="whitespace-nowrap tabular-nums">
        {(row.original.permissions ?? []).length}
      </span>
    )
  },
  {
    accessorKey: 'usersCount',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} name="Usuarios" sortable={false} />
    ),
    cell: ({ row }) => (
      <span className="whitespace-nowrap tabular-nums">{row.original.usersCount ?? 0}</span>
    )
  },
  {
    accessorKey: 'active',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} name="Estado" sortable={false} />
    ),
    cell: ({ row }) => <RoleActiveBadge active={row.original.active} />
  },
  {
    accessorKey: 'updatedAt',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} name="Actualizado" sortable={false} />
    ),
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground">
        {formatHumanDate(row.original.updatedAt)}
      </span>
    )
  }
]
